import { useEffect, useMemo, useRef } from "react";
import type { Chart as ChartJS, ChartData, ChartOptions } from "chart.js";
import { Bar } from "react-chartjs-2";
import { useMediaQuery } from "@/shared/hooks/useMediaQuery";
import { ChartCanvasContainer } from "./Chart.styles";
import { ensureChartsRegistered } from "./chartRegistry";

ensureChartsRegistered();

export interface HorizontalStackedBarChartProps {
  data: ChartData<"bar", number[], string>;
  ariaLabel: string;
  animationKey?: string;
  height?: number;
  options?: ChartOptions<"bar">;
  className?: string;
}

export const HorizontalStackedBarChart = ({
  data,
  ariaLabel,
  animationKey,
  height = 260,
  options,
  className,
}: HorizontalStackedBarChartProps) => {
  const chartRef = useRef<ChartJS<"bar"> | null>(null);
  const prefersReducedMotion = useMediaQuery(
    "(prefers-reduced-motion: reduce)",
  );
  const mergedOptions = useMemo<ChartOptions<"bar">>(
    () => ({
      ...options,
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: "y",
      animation: prefersReducedMotion
        ? false
        : { duration: 550, easing: "easeOutQuart" },
      plugins: {
        ...options?.plugins,
        legend: {
          display: false,
          ...options?.plugins?.legend,
        },
        tooltip: {
          enabled: true,
          padding: 10,
          cornerRadius: 4,
          titleMarginBottom: 6,
          ...options?.plugins?.tooltip,
        },
      },
      scales: {
        x: {
          stacked: true,
          beginAtZero: true,
          grid: { display: false },
          ticks: { display: false },
          border: { display: false },
        },
        y: {
          stacked: true,
          grid: { display: false },
          ticks: {
            color: "#ffffff",
            font: {
              size: 12,
              weight: 500,
              family: "'Poppins', Inter, system-ui, sans-serif",
            },
          },
          border: { color: "rgba(255,255,255,0.75)" },
        },
        ...options?.scales,
      },
    }),
    [options, prefersReducedMotion],
  );

  useEffect(() => {
    if (animationKey === undefined || prefersReducedMotion) return;

    const chart = chartRef.current;
    if (!chart) return;

    chart.stop();
    chart.reset();
    chart.update();
  }, [animationKey, prefersReducedMotion]);

  return (
    <ChartCanvasContainer
      key={animationKey}
      $animate={animationKey !== undefined}
      $height={height}
      className={className}
    >
      <Bar
        ref={chartRef}
        data={data}
        options={mergedOptions}
        role="img"
        aria-label={ariaLabel}
      />
    </ChartCanvasContainer>
  );
};
